import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { format } from 'date-fns';
import { alpha } from '@mui/material/styles';
import AppShell from './components/AppShell';
import api from './services/api';
import { safeDecode } from './utils/decode';
import {
  headCellSx, numeric, pairCellSx, pairRowSx, ProgressBar, SystemChip, tableContainerSx,
} from './quotas/QuotaTable';
import { groupQuotas, sortGroups } from './quotas/quotaGroups';
import { QuotaPairChart, RangeSwitch } from './quotas/trackerCharts';
import {
  fillPct, snapshotsFromHistory, systemSummary, trackerRows, SYSTEM_COLOURS, SYSTEM_LABELS,
} from './quotas/trackerSeries';

const STATUS_COLOURS = { active: 'success', stopped: 'default', expired: 'warning' };
const ON_PACE = 0.5; // points either side of overall that count as on pace

const fmtPts = (v) => `${Math.abs(v).toFixed(1)} pts`;

/** One system's headline figures: completes, overall fill against its target, the last day's change. */
const StatBlock = ({ system, summary }) => {
  const gain = summary.overallThen !== null ? summary.overallNow - summary.overallThen : null;
  const stats = [
    { label: 'Completes', value: summary.completes != null ? summary.completes.toLocaleString() : '—' },
    { label: 'Overall fill', value: `${summary.overallNow.toFixed(1)}%`, note: `of a target of ${summary.target.toLocaleString()}` },
    {
      label: 'Last 24h',
      value: gain === null ? '—' : `${gain >= 0 ? '+' : '−'}${fmtPts(gain)}`,
      note: gain === null ? 'not enough data yet' : 'change in overall fill',
      colour: gain === null ? undefined : gain >= 0 ? 'success.dark' : 'error.main',
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: '1 1 340px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SYSTEM_COLOURS[system] }} />
        <Typography variant="subtitle2">{SYSTEM_LABELS[system]} quotas</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        {stats.map((s) => (
          <Box key={s.label} sx={{ minWidth: 90 }}>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3, color: s.colour }}>{s.value}</Typography>
            {s.note && <Typography variant="caption" color="text.secondary">{s.note}</Typography>}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const paceText = (quota, kind) => {
  const system = SYSTEM_LABELS[quota.system];
  const name = safeDecode(quota.name);
  const value = kind === 'all' ? quota.vsAllTime : quota.vsLastDay;
  if (value === null) return 'Not enough data yet: this needs points at least an hour apart, about a day back.';
  if (kind === 'all') {
    const verdict = Math.abs(value) < ON_PACE
      ? 'It is filling at the same pace as the survey as a whole.'
      : value > 0
        ? `It is ${fmtPts(value)} ahead: filling faster than the survey as a whole, so it may need slowing down.`
        : `It is ${fmtPts(value)} behind: it needs more completes than average to finish on time.`;
    return `${name} (${system}) is ${quota.pctNow.toFixed(0)}% full. ${system} quotas overall are `
      + `${quota.overallNow.toFixed(0)}% of their target. ${verdict}`;
  }
  const verdict = Math.abs(value) < ON_PACE
    ? 'That is the same pace as the survey as a whole.'
    : value > 0 ? `It filled faster than average by ${fmtPts(value)}.` : `It filled slower than average by ${fmtPts(value)}.`;
  return `In the last 24 hours ${name} (${system}) gained ${quota.quotaGain.toFixed(1)} points of fill; `
    + `${system} quotas overall gained ${quota.overallGain.toFixed(1)}. ${verdict}`;
};

/** Ahead of overall in blue, behind in orange, on pace in grey; the tooltip explains in words. */
const PaceCell = ({ quota, kind }) => {
  const value = kind === 'all' ? quota.vsAllTime : quota.vsLastDay;
  let content;
  if (value === null) {
    content = <Typography variant="body2" color="text.disabled">—</Typography>;
  } else if (Math.abs(value) < ON_PACE) {
    content = <Typography variant="body2" color="text.secondary">On pace</Typography>;
  } else {
    const ahead = value > 0;
    const Icon = ahead ? ArrowUpwardRoundedIcon : ArrowDownwardRoundedIcon;
    content = (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', color: ahead ? 'info.dark' : 'warning.dark' }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
          {Math.abs(value).toFixed(1)} {ahead ? 'ahead' : 'behind'}
        </Typography>
      </Box>
    );
  }
  return <Tooltip title={paceText(quota, kind)}><Box component="span">{content}</Box></Tooltip>;
};

/** Whether the email draft must mention this quota: Auto lets Claude decide. */
const toggleSx = { textTransform: 'none', py: 0.1, px: 0.7, fontSize: 12, whiteSpace: 'nowrap' };
const EmailToggle = ({ value, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={value || 'auto'} aria-label="In the email draft"
    onChange={(e, v) => v && onChange(v === 'auto' ? undefined : v)} sx={{ flexWrap: 'nowrap' }}>
    <ToggleButton value="auto" sx={toggleSx}>Auto</ToggleButton>
    <ToggleButton value="include" color="success" sx={toggleSx}>Include</ToggleButton>
    <ToggleButton value="exclude" color="error" sx={toggleSx}>Leave out</ToggleButton>
  </ToggleButtonGroup>
);

const HeaderLabel = ({ label, tooltip }) => (tooltip ? (
  <Tooltip title={tooltip} placement="top" arrow>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label}<InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
    </Box>
  </Tooltip>
) : label);

const QuotaTrackerDetail = () => {
  const { surveyId, trackerId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [tracker, setTracker] = useState(null);
  // Alchemer quotas: Ranger's hourly snapshots. Wrangler quotas: Wrangler's history, as snapshots.
  const [series, setSeries] = useState({ alchemer: [], wrangler: [] });
  const [wranglerError, setWranglerError] = useState(null);
  const [missingWrangler, setMissingWrangler] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [range, setRange] = useState('all');
  const [openCharts, setOpenCharts] = useState({});
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const toggleChart = (groupKey) => setOpenCharts((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  const [whyOpen, setWhyOpen] = useState(false);

  const [emailOverrides, setEmailOverrides] = useState({});
  const [draftOpen, setDraftOpen] = useState(false);
  const [templateEmail, setTemplateEmail] = useState('');
  const [draftedEmail, setDraftedEmail] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));
    api.get(`/surveys/${surveyId}/quota-trackers/`).then((res) => {
      const found = res.data.find((t) => String(t.id) === String(trackerId));
      if (!found) return;
      setTracker(found);
      api.get(`/quota-monitors/${found.quota_monitor}/snapshots/`).then((sRes) => {
        setSeries((prev) => ({ ...prev, alchemer: sRes.data }));
      });
      // Each system's failure is its own: Wrangler down still shows the Alchemer quotas.
      api.get(`/surveys/${surveyId}/quota-trackers/${trackerId}/wrangler-history/`)
        .then((hRes) => {
          setSeries((prev) => ({ ...prev, wrangler: snapshotsFromHistory(hRes.data.wrangler) }));
          setMissingWrangler(hRes.data.missing || []);
        })
        .catch((err) => setWranglerError(err?.response?.data?.error || "Couldn't load the Wrangler quotas' history."));
    });
  }, [surveyId, trackerId]);

  const summaries = useMemo(() => ({
    alchemer: systemSummary(series.alchemer),
    wrangler: systemSummary(series.wrangler),
  }), [series]);
  const trackedSystems = Object.keys(summaries).filter((s) => summaries[s]);
  const mixed = trackedSystems.length > 1;

  const rows = useMemo(() => trackerRows(series), [series]);
  const groups = useMemo(() => sortGroups(groupQuotas(rows), orderBy, order), [rows, orderBy, order]);

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const handleDraft = async () => {
    setDrafting(true);
    setDraftedEmail('');
    try {
      // One overall per system: an Alchemer quota and its Wrangler copy have separate targets.
      const overall = Object.fromEntries(trackedSystems.map((s) => [s, summaries[s].overallNow]));
      const payload = {
        overall_fill: mixed ? overall : overall[trackedSystems[0]] ?? null,
        template_email: templateEmail,
        quotas: groups.flat().map((q) => ({
          name: safeDecode(q.name),
          fill_pct: fillPct(q.responses, q.limit),
          vs_overall: q.vsAllTime ?? null,
          responses: q.responses,
          limit: q.limit,
          force_include: emailOverrides[q.key] === 'include',
          force_exclude: emailOverrides[q.key] === 'exclude',
        })),
      };
      const res = await api.post(`/surveys/${surveyId}/quota-trackers/${trackerId}/draft-email/`, payload);
      setDraftedEmail(res.data.email);
    } catch {
      setDraftedEmail('Failed to generate email. Please try again.');
    } finally {
      setDrafting(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(draftedEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!survey || !tracker) return null;

  const qmName = survey.quota_monitors?.find((q) => q.id === tracker.quota_monitor)?.quota_view_name
    ?? `View #${tracker.quota_monitor}`;

  const columns = [
    { id: 'expand', label: 'Chart', unsortable: true, width: 72 },
    ...(mixed ? [{ id: 'system', label: 'System' }] : []),
    { id: 'name', label: 'Quota', minWidth: 110 },
    { id: 'responses', label: 'Completes', numeric: true },
    { id: 'limit', label: 'Limit', numeric: true },
    { id: 'progress', label: 'Fill', minWidth: 130 },
    {
      id: 'vsAllTime', label: 'vs Overall (pts)',
      tooltip: "How this quota's fill compares with the overall fill of its system's quotas. Ahead: filling faster than the survey as a whole. Behind: it needs a push.",
    },
    {
      id: 'vsLastDay', label: 'Last 24h (pts)',
      tooltip: 'The same comparison for the last 24 hours only: is this quota catching up or falling behind?',
    },
    {
      id: 'email', label: 'In email', unsortable: true,
      tooltip: 'For the email draft: Auto lets Claude decide from the fill rates; Include or Leave out decides for it.',
    },
  ];

  return (
    <AppShell maxWidth="xl">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Link to={`/survey/${surveyId}/quota-tracker`} style={{ textDecoration: 'none', color: 'inherit' }}>Quota Tracker</Link>
        <Typography>{qmName}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <TimelineRoundedIcon color="secondary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>{qmName}</Typography>
        <Chip label={tracker.status} color={STATUS_COLOURS[tracker.status]} size="small" sx={{ ml: 1 }} />
        <Button
          variant="outlined" size="small" startIcon={<MailOutlineIcon />} onClick={() => setDraftOpen(true)}
          disabled={rows.length === 0} sx={{ ml: 'auto', textTransform: 'none' }}
        >
          Draft email
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tracker.snapshot_count.toLocaleString()} Alchemer snapshot{tracker.snapshot_count !== 1 ? 's' : ''}
        {' · '}Started {format(new Date(tracker.started_at), 'd MMM yyyy, HH:mm')}
        {' · '}Expires {format(new Date(tracker.expires_at), 'd MMM yyyy, HH:mm')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {trackedSystems.map((system) => <StatBlock key={system} system={system} summary={summaries[system]} />)}
      </Box>

      {mixed && (
        <Alert severity="info" sx={{ mb: 2, py: 0.25 }}>
          Each system is compared with its own overall.{' '}
          <Button color="inherit" size="small" onClick={() => setWhyOpen(!whyOpen)}
            sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 0, verticalAlign: 'baseline', fontSize: 'inherit' }}>
            {whyOpen ? 'Hide' : 'Why the two can differ'}
          </Button>
          <Collapse in={whyOpen}>
            <Box sx={{ mt: 0.5 }}>
              Alchemer figures come from hourly snapshots, as they were at the time. Wrangler figures are recalculated
              from today&apos;s counted completes, so a complete removed later doesn&apos;t appear at any point, and Wrangler
              stops a quota at its limit where Alchemer may run over.
            </Box>
          </Collapse>
        </Alert>
      )}
      {wranglerError && <Alert severity="warning" sx={{ mb: 2 }}>Wrangler quotas are missing: {wranglerError}</Alert>}
      {missingWrangler.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Wrangler no longer has quota{missingWrangler.length !== 1 ? 's' : ''} {missingWrangler.join(', ')}, so
          {missingWrangler.length !== 1 ? ' they are' : ' it is'} left out.
        </Alert>
      )}

      {rows.length === 0 ? (
        <Typography color="textSecondary">
          No data yet. Alchemer quotas get their first snapshot within the hour; Wrangler quotas show as soon as Wrangler answers.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ color: 'info.dark', fontWeight: 600 }}>Ahead</Box>: filling faster than overall.{' '}
              <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>Behind</Box>: needs a push. Click a quota to see its chart, both systems together.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Charts:</Typography>
              <RangeSwitch value={range} onChange={setRange} />
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col, i) => (
                    <TableCell key={col.id} sx={{ ...headCellSx(col, i, columns.length), px: 1.25 }}>
                      {col.unsortable ? <HeaderLabel label={col.label} tooltip={col.tooltip} /> : (
                        <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleSort(col.id)}>
                          <HeaderLabel label={col.label} tooltip={col.tooltip} />
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group, g) => {
                  const groupKey = group[0].key;
                  const open = Boolean(openCharts[groupKey]);
                  const finalGroup = g === groups.length - 1;
                  return (
                    <Fragment key={groupKey}>
                      {group.map((quota, r) => {
                        const last = r === group.length - 1;
                        // Ten columns: slightly tighter padding than the monitoring table, to fit.
                        const cell = { ...pairCellSx(last && !open), px: 1.25 };
                        const label = `${open ? 'Hide' : 'Show'} the chart for ${safeDecode(quota.name)}`;
                        return (
                          // The whole pair is one target: either row opens the chart they share.
                          <TableRow
                            key={quota.key}
                            onClick={() => toggleChart(groupKey)}
                            onMouseEnter={() => setHoveredGroup(groupKey)}
                            onMouseLeave={() => setHoveredGroup(null)}
                            sx={{
                              ...pairRowSx(g, finalGroup && last && !open),
                              cursor: 'pointer',
                              ...(hoveredGroup === groupKey && { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }),
                            }}
                          >
                            {r === 0 && (
                              <TableCell
                                rowSpan={group.length}
                                sx={{ ...pairCellSx(!open), verticalAlign: 'middle', ...(finalGroup && !open && { borderBottom: 'none' }) }}
                              >
                                <Tooltip title={label}>
                                  <Button
                                    size="small"
                                    aria-label={label}
                                    aria-expanded={open}
                                    startIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    onClick={(e) => { e.stopPropagation(); toggleChart(groupKey); }}
                                    sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0, px: 0.5, '& .MuiButton-startIcon': { mr: 0.25 } }}
                                  >
                                    {open ? 'Hide' : 'Show'}
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            )}
                            {mixed && <TableCell sx={cell}><SystemChip system={quota.system} /></TableCell>}
                            <TableCell sx={{ ...cell, whiteSpace: 'nowrap', fontWeight: r === 0 ? 500 : 400, color: r === 0 ? 'text.primary' : 'grey.600' }}>
                              {safeDecode(quota.name)}
                            </TableCell>
                            <TableCell sx={{ ...cell, ...numeric }}>{quota.responses.toLocaleString()}</TableCell>
                            <TableCell sx={{ ...cell, ...numeric }}>{quota.limit.toLocaleString()}</TableCell>
                            <TableCell sx={cell}><ProgressBar quota={quota} /></TableCell>
                            <TableCell sx={cell}><PaceCell quota={quota} kind="all" /></TableCell>
                            <TableCell sx={cell}><PaceCell quota={quota} kind="day" /></TableCell>
                            <TableCell sx={cell} onClick={(e) => e.stopPropagation()}>
                              <EmailToggle
                                value={emailOverrides[quota.key]}
                                onChange={(v) => setEmailOverrides((prev) => ({ ...prev, [quota.key]: v }))}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {open && (
                        <TableRow sx={pairRowSx(g, finalGroup)}>
                          <TableCell colSpan={columns.length} sx={{ ...pairCellSx(true), px: 3, pb: 2 }}>
                            <QuotaPairChart rows={group} series={series} rangeId={range} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={draftOpen} onClose={() => setDraftOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MailOutlineIcon color="secondary" />
          Draft Outreach Email
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Template email (optional)"
            helperText="Paste one of your old emails so Claude matches your usual tone, greeting, and sign-off style"
            multiline
            rows={4}
            fullWidth
            value={templateEmail}
            onChange={(e) => setTemplateEmail(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleDraft}
            disabled={drafting}
            startIcon={drafting ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />}
          >
            {drafting ? 'Drafting…' : 'Generate Draft'}
          </Button>
          {draftedEmail && (
            <Box sx={{ mt: 3 }}>
              <TextField
                multiline
                fullWidth
                value={draftedEmail}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                minRows={8}
              />
              <Button startIcon={<ContentCopyIcon />} onClick={handleCopy} sx={{ mt: 1 }} size="small">
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default QuotaTrackerDetail;
